import { ChangeEventHandler, Fragment, memo, useState } from 'react';

import clsx from 'clsx';
import { useAtomValue } from 'jotai/index';
import { isEmpty, isNullish } from 'remeda';
import useSWR from 'swr';

import TextField from '@mui/material/TextField';

import { CreateRootFolderButton } from './components/createRootFolderButton/CreateRootFolderButton';
import { FileSystemSearchResults } from './components/fileSystemSearchResults/FileSystemSearchResults';
import { FileSystemTable } from './components/fileSystemTable/FileSystemTable';
import { options, SearchType } from './constants';
import { SearchForm } from './form';

import css from './FileSystem.module.css';

import { fileSystemService } from 'v2/api/fileSystemService';
import { selectedFileAtom } from 'v2/atoms/fileSystemAtom';
import { Card } from 'v2/components/card/Card';
import { EmptyPlaceholder } from 'v2/components/emptyPlaceholder/EmptyPlaceholder';
import { Loader } from 'v2/components/loader/Loader';
import { SearchTextField } from 'v2/components/searchTextField/SearchTextField';
import { Select } from 'v2/components/select/Select';
import { useDebounce } from 'v2/hooks/useDebounce';

export const FileSystem = memo(() => {
  const selectedFile = useAtomValue(selectedFileAtom);

  const { data, isLoading, mutate } = useSWR('getRootFolders', fileSystemService.getRootFolders);

  const [searchForm, setSearchForm] = useState<SearchForm>({
    search: '',
    type: SearchType.Folder,
  });

  const search = useDebounce(searchForm.search, 600);

  const handleSearchChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    setSearchForm((prev) => ({ ...prev, search: e.target.value }));
  };

  const handleSelectType = (type: SearchType) => {
    setSearchForm((prev) => ({ ...prev, type }));
  };

  const handleClearSearch = () => {
    setSearchForm((prev) => ({ ...prev, search: '' }));
  };

  return (
    <div className={css.fileSystem}>
      <Card className={css.fileSystemOptionsContainer}>
        <div className={clsx('form-group', css.fileSystemOptions)}>
          <Select value={searchForm.type} options={options} label="Type" onChange={handleSelectType} size="small" />

          <SearchTextField
            value={searchForm.search}
            onChange={handleSearchChange}
            onClear={handleClearSearch}
            label="Search"
          />

          <TextField
            value={isNullish(selectedFile) ? 'No File Selected' : selectedFile?.name}
            label="Selected File"
            slotProps={{ input: { readOnly: true } }}
            disabled={isNullish(selectedFile)}
            size="small"
          />
        </div>

        <CreateRootFolderButton onSuccess={mutate} />
      </Card>

      {!isEmpty(search) ? (
        <FileSystemSearchResults search={search} type={searchForm.type} />
      ) : (
        <Fragment>
          {isLoading ? (
            <Loader />
          ) : (
            <Fragment>
              {isNullish(data) || isEmpty(data) ? <EmptyPlaceholder /> : <FileSystemTable folders={data} />}
            </Fragment>
          )}
        </Fragment>
      )}
    </div>
  );
});

FileSystem.displayName = 'FileSystem';
